import type * as zod from 'zod';

import type { CartesianPose } from '@sb/geometry';
import * as log from '@sb/log';
import type { ArmTarget } from '@sb/motion-planning';

import { FailureKind } from '../../FailureKind';
import type { RoutineContext } from '../../RoutineContext';
import type { StepFailure } from '../../types';
import type { StepPlayArguments } from '../Step';
import Step from '../Step';

import Arguments, { type PoseSource } from './Arguments';
import { generateWaypointsByLevel, resolveSeed } from './sampling';
import Variables from './Variables';

type Arguments = zod.infer<typeof Arguments>;
type Variables = zod.infer<typeof Variables>;

const ns = log.namespace('SamplePositioningApproachStep');

export default class SamplePositioningApproachStep extends Step<
  Arguments,
  Variables
> {
  public static areSubstepsRequired = false;

  public static Arguments = Arguments;

  public static Variables = Variables;

  private motion?: ReturnType<RoutineContext['doMotion']>;

  private stopped = false;

  protected initializeVariableState(): void {
    this.variables = {
      generatedWaypointCount: 0,
      seedUsed: 0,
    };
  }

  // Resolve a pose source to a live CartesianPose. For space references we
  // always read through routineContext at play time so that edits made to the
  // referenced Space after the step was configured take effect on the next
  // run (the step persists the ID, not a snapshot).
  private resolvePose(source: PoseSource, fieldName: string): CartesianPose {
    if ('positionListID' in source) {
      const entry = this.routineFrame.getPositionListEntry(
        source.positionListID,
        0,
      );

      return entry.pose;
    }

    if (!source) {
      throw new Error(`${fieldName} is not configured`);
    }

    return source;
  }

  public async _play({ fail }: StepPlayArguments): Promise<void> {
    this.stopped = false;

    // Set the TCP offset so the target/starting poses are interpreted at the
    // tooltip the operator chose (wrist flange or an end-effector TCP). Must
    // happen before the initial move so doMotion uses the right frame.
    this.routineContext.setTCPOffsetOption(this.args.tcpOption);

    let targetPose: CartesianPose;
    let startingPose: CartesianPose;

    try {
      targetPose = this.resolvePose(this.args.targetPose, 'targetPose');
      startingPose = this.resolvePose(this.args.startingPose, 'startingPose');
    } catch (error) {
      return fail({
        failure: { kind: FailureKind.InvalidRoutineLoadedFailure },
        failureReason: (error as Error).message,
        error: error as Error,
      });
    }

    if (
      targetPose.x === startingPose.x &&
      targetPose.y === startingPose.y &&
      targetPose.z === startingPose.z
    ) {
      return fail({
        failure: { kind: FailureKind.InvalidRoutineLoadedFailure },
        failureReason:
          'Starting position must differ from target position to define an approach axis',
      });
    }

    // Approach length is the Euclidean distance from the target to the
    // starting pose. The starting pose just defines the cone axis direction
    // and length — the user picks a representative starting pose, and the
    // approach geometry is derived. Computed here (not at step-config time)
    // so that updates to a referenced Space take effect on the next run.
    const dx = startingPose.x - targetPose.x;
    const dy = startingPose.y - targetPose.y;
    const dz = startingPose.z - targetPose.z;
    const approachLength = Math.sqrt(dx * dx + dy * dy + dz * dz);

    const seedUsed = resolveSeed(this.args.randomSeed);

    const levels = generateWaypointsByLevel({
      targetPose,
      startingPose,
      approachLength,
      bufferLength: this.args.bufferLength,
      narrowRadius: this.args.narrowRadius,
      wideRadius: this.args.wideRadius,
      radiusMode: this.args.radiusMode,
      levels: this.args.levels,
      heightMode: this.args.heightMode,
      heightSpread: this.args.heightSpread,
      sampleCount: this.args.sampleCount,
      levelCount: this.args.levelCount,
      targetEmphasis: this.args.targetEmphasis,
      orientationJitter: this.args.orientationJitter,
      lockedAxes: this.args.lockedAxes,
      seedUsed,
    });

    const totalWaypointCount = levels.reduce((n, level) => n + level.length, 0);

    log.info(
      ns`_play`,
      `generated ${totalWaypointCount} waypoints across ${levels.length} levels (seed=${seedUsed})`,
    );

    this.variables = {
      generatedWaypointCount: totalWaypointCount,
      seedUsed,
    };

    // First motion: move to the target pose. Done as its own command (not
    // blended with the sweep) so the robot reaches a clean, deterministic
    // starting context before approach data collection begins. Joint motion
    // because the current pose can be arbitrary.
    const initialMotion = this.routineContext.doMotion({
      request: {
        targets: [
          {
            motionKind: 'joint',
            pose: targetPose,
            blendRadius: 0,
          },
        ],
      },
      speedProfiles: [this.args.speedProfile],
      useCache: false,
    });

    this.motion = initialMotion;

    const initialResult = await initialMotion.race(
      'failure',
      'complete',
      'cancelled',
    );

    initialMotion.removeAllListeners();
    this.motion = undefined;

    if (initialResult) {
      return fail(initialResult as StepFailure);
    }

    // race() resolves with undefined for both 'complete' and 'cancelled' (their
    // event payloads are void), so we must consult the explicit stop flag set
    // by _stop() to avoid proceeding into the sweep after a cancel.
    if (this.stopped) {
      return;
    }

    // Sweep: one motion plan per level. Splitting at level boundaries keeps
    // each plan small enough for the planner to solve quickly; the cost is
    // that the robot briefly stops at each boundary instead of blending
    // through the whole sweep. Waypoints inside a level still blend so the
    // intra-level motion stays smooth.
    for (let levelIdx = 0; levelIdx < levels.length; levelIdx += 1) {
      if (this.stopped) return;

      const level = levels[levelIdx];
      if (level.length === 0) continue;

      const targets: ArmTarget[] = level.map((pose, idx) => ({
        motionKind: this.args.motionKind,
        pose,
        blendRadius: idx === level.length - 1 ? 0 : this.args.blendRadius,
      }));

      const speedProfiles = level.map(() => this.args.speedProfile);

      const motion = this.routineContext.doMotion({
        request: {
          targets: targets as [ArmTarget, ...ArmTarget[]],
        },
        speedProfiles,
        useCache: false,
      });

      this.motion = motion;

      const result = await motion.race('failure', 'complete', 'cancelled');
      motion.removeAllListeners();
      this.motion = undefined;

      if (result) {
        return fail(result as StepFailure);
      }
    }
  }

  public _pause(): void {
    this.motion?.emit('pause');
  }

  public _resume(): void {
    this.motion?.emit('resume');
  }

  public _stop(): void {
    this.stopped = true;
    this.motion?.emit('cancel');
  }
}
